import { useState } from 'react'
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
} from '@mui/material'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import { dishaLogo } from '../../assets/index'
import { mainNavLinks } from '../../data/navigation'
import PrimaryButton from './PrimaryButton'
import {
  HEADER_MOBILE_MQ,
  headerContactButtonSx,
  headerHeight,
  headerLogoSize,
  headerNavGap,
  headerPaddingX,
} from './headerLayout'
import { mobileButtonSx } from '../../theme/mobileLayout'

function FigmaMenuIcon() {
  return (
    <Box
      aria-hidden
      sx={{
        width: 18,
        height: 12,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      {[0, 1, 2].map((line) => (
        <Box
          key={line}
          sx={{
            width: '100%',
            height: '2px',
            bgcolor: '#111',
            borderRadius: '1px',
          }}
        />
      ))}
    </Box>
  )
}

function LogoLink({ sx = {} }) {
  return (
    <Box
      component={RouterLink}
      to="/"
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        textDecoration: 'none',
        ...sx,
      }}
    >
      <Box
        component="img"
        src={dishaLogo}
        alt="Disha Foundation"
        sx={{
          width: headerLogoSize.width,
          height: headerLogoSize.height,
          display: 'block',
        }}
      />
    </Box>
  )
}

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()

  const isActive = (path) => location.pathname === path

  const navLinkSx = (path) => ({
    color: isActive(path) ? '#082753' : '#111',
    fontSize: 18,
    lineHeight: '20px',
    textDecoration: 'none',
    fontWeight: 400,
    whiteSpace: 'nowrap',
    '&:hover': { opacity: 0.7 },
  })

  const drawer = (
    <Box
      sx={{
        width: '100%',
        maxWidth: 362,
        minWidth: 280,
        pt: 3,
        px: '20px',
        pb: 4,
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <LogoLink />
        <IconButton
          onClick={() => setMobileOpen(false)}
          aria-label="close menu"
          sx={{ width: 48, height: 48, p: 0, fontSize: 28, lineHeight: 1, color: '#111' }}
        >
          ×
        </IconButton>
      </Box>

      <List sx={{ p: 0 }}>
        {mainNavLinks.map((link) => (
          <ListItem key={link.path} disablePadding sx={{ mb: '4px' }}>
            <ListItemButton
              component={RouterLink}
              to={link.path}
              onClick={() => setMobileOpen(false)}
              sx={{
                px: 0,
                py: '12px',
                color: isActive(link.path) ? '#082753' : '#111',
              }}
            >
              <ListItemText
                primary={link.label}
                primaryTypographyProps={{
                  fontSize: 18,
                  lineHeight: 'normal',
                  fontWeight: 400,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Box sx={{ mt: 4 }}>
        <PrimaryButton
          to="/contact"
          showArrow={false}
          sx={mobileButtonSx}
        >
          Contact Us
        </PrimaryButton>
      </Box>
    </Box>
  )

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'rgba(248, 249, 250, 0.95)',
          backdropFilter: 'blur(10px)',
          color: '#111',
          minWidth: 320,
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            minHeight: headerHeight,
            height: headerHeight,
            px: headerPaddingX,
            boxSizing: 'border-box',
            [HEADER_MOBILE_MQ]: {
              minHeight: 72,
              height: 72,
              px: '20px',
            },
          }}
        >
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              width: '100%',
              alignItems: 'center',
              justifyContent: 'space-between',
              minHeight: 48,
              [HEADER_MOBILE_MQ]: { minHeight: 48 },
            }}
          >
            <LogoLink />
            <IconButton
              onClick={() => setMobileOpen(true)}
              aria-label="open menu"
              sx={{
                width: 48,
                height: 48,
                p: 0,
                color: '#111',
                flexShrink: 0,
              }}
            >
              <FigmaMenuIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: { xs: 'none', md: 'grid' },
              gridTemplateColumns: '1fr auto 1fr',
              alignItems: 'center',
              width: '100%',
              minHeight: 48,
            }}
          >
            <Box
              component="nav"
              sx={{
                display: 'flex',
                gap: headerNavGap,
                alignItems: 'center',
                justifySelf: 'start',
              }}
            >
              {mainNavLinks.map((link) => (
                <Box key={link.path} component={RouterLink} to={link.path} sx={navLinkSx(link.path)}>
                  {link.label}
                </Box>
              ))}
            </Box>

            <LogoLink sx={{ justifySelf: 'center' }} />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', justifySelf: 'end' }}>
              <PrimaryButton to="/contact" showArrow={false} sx={headerContactButtonSx}>
                Contact Us
              </PrimaryButton>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { md: 'none' },
          '& .MuiDrawer-paper': {
            width: { xs: 'min(100vw, 402px)', sm: 360 },
            maxWidth: '100vw',
            boxSizing: 'border-box',
            bgcolor: 'rgba(248, 249, 250, 0.98)',
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  )
}
